/**
 * Validation utilities for Event Detection configuration
 */

import { SPEED_METRIC_TYPE } from './formatConverters';
import { isRangeOperator } from './eventConfigTransform';

export const createValidationError = (key, defaultValue, values = {}) => ({
  key,
  defaultValue,
  values
});

const validationError = (key, defaultValue, values) =>
  createValidationError(`sensors.eventDetection.validation.${key}`, defaultValue, values);

/**
 * Serialize a condition to a comparable string for duplicate detection
 * @param {Object} condition - The condition object
 * @returns {string} - Serialized condition string
 */
/**
 * JSON.stringify with sorted keys at every level.
 *
 * Not JSON.stringify(value, Object.keys(value).sort()): a replacer ARRAY filters
 * keys recursively, so the numbers inside a range threshold's min/max were
 * stripped and every range condition serialised to {"max":{},"min":{}}. Two
 * ranges with different bounds then looked identical and were reported as
 * duplicates.
 */
const stableStringify = (value) => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) || 'null';
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const body = Object.keys(value).sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',');
  return `{${body}}`;
};

const serializeCondition = (condition) => {
  if (!condition) return '';

  const { metric, metricType, operator, thresholds, axis } = condition;

  // Sort axis array to normalize [1,2,3] vs [3,2,1]
  const sortedAxis = axis && axis.length > 0 ? [...axis].sort().join(',') : '';

  const thresholdsStr = stableStringify(thresholds);

  return `${metric}|${metricType}|${operator}|${sortedAxis}|${thresholdsStr}`;
};

/**
 * Check if two conditions are duplicates
 * @param {Object} cond1 - First condition
 * @param {Object} cond2 - Second condition
 * @returns {boolean} - True if conditions are duplicates
 */
export const areConditionsDuplicate = (cond1, cond2) => serializeCondition(cond1) === serializeCondition(cond2);

/**
 * Find duplicate conditions within an event config
 * @param {Array} conditions - Array of condition objects
 * @returns {Array} - Array of indices that have duplicates
 */
export const findDuplicateConditions = (conditions) => {
  if (!conditions || conditions.length < 2) return [];

  const duplicateIndices = new Set();
  const seen = new Map();

  conditions.forEach((condition, index) => {
    const serialized = serializeCondition(condition);

    if (seen.has(serialized)) {
      // Mark both original and duplicate
      duplicateIndices.add(seen.get(serialized));
      duplicateIndices.add(index);
    } else {
      seen.set(serialized, index);
    }
  });

  return Array.from(duplicateIndices);
};

/**
 * Validate min/max threshold values
 * @param {Object} thresholds - Threshold object
 * @param {string} operator - Operator type
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateThresholds = (thresholds, operator) => {
  const errors = [];

  if (!thresholds) {
    errors.push(validationError('thresholdsRequired', 'Thresholds are required'));
    return { isValid: false, errors };
  }

  if (isRangeOperator(operator)) {
    const { min, max } = thresholds;

    if (!min || !max) {
      errors.push(validationError('rangeValuesRequired', 'Both min and max values are required for range operators'));
      return { isValid: false, errors };
    }

    // Check if triaxial (has x, y, z keys)
    const hasTriaxial = min.x !== undefined || min.y !== undefined || min.z !== undefined;

    if (hasTriaxial) {
      // Validate each axis that exists
      ['x', 'y', 'z'].forEach((axis) => {
        if (min[axis] !== undefined && max[axis] !== undefined) {
          const minVal = parseFloat(min[axis]);
          const maxVal = parseFloat(max[axis]);

          if (Number.isNaN(minVal) || Number.isNaN(maxVal)) {
            errors.push(validationError('invalidNumericAxis', 'Invalid numeric value for {{axis}} axis', { axis: axis.toUpperCase() }));
          } else if (minVal >= maxVal) {
            errors.push(validationError('minLessThanMaxAxis', 'Min value must be less than max value for {{axis}} axis ({{min}} >= {{max}})', {
              axis: axis.toUpperCase(),
              min: minVal,
              max: maxVal
            }));
          }
        }
      });
    } else {
      // Single value
      const minVal = parseFloat(min.value);
      const maxVal = parseFloat(max.value);

      if (Number.isNaN(minVal) || Number.isNaN(maxVal)) {
        errors.push(validationError('invalidNumericThreshold', 'Invalid numeric value for threshold'));
      } else if (minVal >= maxVal) {
        errors.push(validationError('minLessThanMax', 'Min value must be less than max value ({{min}} >= {{max}})', {
          min: minVal,
          max: maxVal
        }));
      }
    }
  } else {
    // Non-range operators - just validate that values are numbers
    const hasTriaxial = thresholds.x !== undefined || thresholds.y !== undefined || thresholds.z !== undefined;

    if (hasTriaxial) {
      ['x', 'y', 'z'].forEach((axis) => {
        if (thresholds[axis] !== undefined) {
          const val = parseFloat(thresholds[axis]);
          if (Number.isNaN(val)) {
            errors.push(validationError('invalidNumericAxis', 'Invalid numeric value for {{axis}} axis', { axis: axis.toUpperCase() }));
          }
        }
      });
    } else if (thresholds.value !== undefined) {
      const val = parseFloat(thresholds.value);
      if (Number.isNaN(val)) {
        errors.push(validationError('invalidNumericThreshold', 'Invalid numeric value for threshold'));
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Collect the numeric operands of a threshold. Speed is single-axis, so this
 * only has to handle the single value and the min/max pair.
 */
const thresholdValues = (thresholds, operator) => {
  if (!thresholds) return [];

  if (isRangeOperator(operator)) {
    return [thresholds.min, thresholds.max]
      .filter(bound => bound && bound.value !== undefined)
      .map(bound => parseFloat(bound.value));
  }

  if (thresholds.value === undefined) return [];
  return [parseFloat(thresholds.value)];
};

/**
 * Every speed threshold in a set of saved configs, as numbers.
 *
 * @param {Array} configs - event_configs as the server last sent them
 * @returns {Array<number>}
 */
export const collectSpeedValues = configs => (configs || []).reduce(
  (values, config) => values.concat(
    ((config && config.conditions) || [])
      .filter(condition => condition.metricType === SPEED_METRIC_TYPE)
      .reduce((acc, condition) => acc.concat(
        thresholdValues(condition.thresholds, condition.operator)
      ), [])
  ),
  []
);

/**
 * Speed is the only parameter with a band the device can actually detect.
 * The backend rejects an out-of-band value too, and names the band in the
 * user's units; this check flags the row inline before the save.
 *
 * A value still equal to a saved one is skipped. The displayed value is
 * rounded to nearest and speed_limits is rounded inward, so a stored value
 * near the floor can show just outside the band; the backend keeps the stored
 * value for an unchanged echo, so blocking it would lock the mote.
 *
 * @param {Object} condition - The condition object
 * @param {Object} speedLimits - { min, max, unit } for this mote, from the API
 * @param {Array<number>} savedSpeedValues - speed values as the server sent them
 * @returns {Array} - validation errors
 */
export const validateSpeedThresholds = (condition, speedLimits, savedSpeedValues = []) => {
  if (!speedLimits || condition.metricType !== SPEED_METRIC_TYPE) {
    return [];
  }

  const outOfBand = thresholdValues(condition.thresholds, condition.operator)
    .some(value => !Number.isNaN(value)
      && savedSpeedValues.indexOf(value) === -1
      && (value < speedLimits.min || value > speedLimits.max));

  if (!outOfBand) return [];

  return [validationError(
    'speedOutOfBand',
    'Speed must be between {{min}} and {{max}} {{unit}}',
    { min: speedLimits.min, max: speedLimits.max, unit: speedLimits.unit }
  )];
};

/**
 * Validate a single condition
 * @param {Object} condition - The condition object
 * @param {Object} speedLimits - { min, max, unit } for this mote, from the API
 * @param {Array<number>} savedSpeedValues - speed values as the server sent them
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateCondition = (condition, speedLimits = null, savedSpeedValues = []) => {
  const errors = [];

  if (!condition) {
    errors.push(validationError('conditionRequired', 'Condition is required'));
    return { isValid: false, errors };
  }

  if (!condition.metric) {
    errors.push(validationError('metricRequired', 'Metric is required'));
  }

  if (!condition.metricType) {
    errors.push(validationError('metricTypeRequired', 'Metric type is required'));
  }

  if (!condition.operator) {
    errors.push(validationError('operatorRequired', 'Operator is required'));
  }

  // Validate thresholds
  const thresholdValidation = validateThresholds(condition.thresholds, condition.operator);
  if (!thresholdValidation.isValid) {
    errors.push(...thresholdValidation.errors);
  } else {
    errors.push(...validateSpeedThresholds(condition, speedLimits, savedSpeedValues));
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Validate an event config (contains multiple conditions)
 * @param {Object} config - Event config object
 * @returns {Object} - { isValid: boolean, errors: Array, conditionErrors: Object }
 */
export const validateEventConfig = (config, speedLimits = null, savedSpeedValues = []) => {
  const errors = [];
  const conditionErrors = {}; // Map of condition index to array of errors

  if (!config) {
    errors.push(validationError('eventConfigRequired', 'Event config is required'));
    return { isValid: false, errors, conditionErrors };
  }

  // Check that at least one condition exists
  if (!config.conditions || config.conditions.length === 0) {
    errors.push(validationError('oneConditionRequired', 'At least one condition is required'));
    return { isValid: false, errors, conditionErrors };
  }

  // Validate each condition
  config.conditions.forEach((condition, index) => {
    const validation = validateCondition(condition, speedLimits, savedSpeedValues);
    if (!validation.isValid) {
      conditionErrors[index] = validation.errors;
    }
  });

  // Check for duplicate conditions
  const duplicateIndices = findDuplicateConditions(config.conditions);
  if (duplicateIndices.length > 0) {
    errors.push(validationError('duplicateConditions', 'Duplicate conditions found at positions: {{positions}}', {
      positions: duplicateIndices.map(i => i + 1).join(', ')
    }));

    // Add errors to each duplicate condition
    duplicateIndices.forEach((index) => {
      if (!conditionErrors[index]) {
        conditionErrors[index] = [];
      }
      conditionErrors[index].push(validationError('conditionDuplicate', 'This condition is a duplicate'));
    });
  }

  // Validate logic
  if (config.conditions.length > 1 && !['AND', 'OR'].includes(config.logic)) {
    errors.push(validationError('invalidLogicOperator', 'Invalid logic operator'));
  }

  // Validate action
  if (!config.action) {
    errors.push(validationError('actionRequired', 'Action is required'));
  }

  const hasConditionErrors = Object.keys(conditionErrors).length > 0;

  return {
    isValid: errors.length === 0 && !hasConditionErrors,
    errors,
    conditionErrors
  };
};

/**
 * Validate entire event detection configuration
 * @param {Object} eventDetection - Event detection object with event_configs array
 * @param {boolean} enabled - Whether event detection is enabled
 * @returns {Object} - { isValid: boolean, errors: Array, configErrors: Object }
 */
export const validateEventDetection = (
  eventDetection, enabled, speedLimits = null, savedSpeedValues = []
) => {
  const errors = [];
  const configErrors = {}; // Map of config index to validation result

  if (!enabled) {
    // If disabled, no validation needed
    return { isValid: true, errors: [], configErrors: {} };
  }

  if (!eventDetection) {
    errors.push(validationError('eventDetectionRequiredWhenEnabled', 'Event detection configuration is required when enabled'));
    return { isValid: false, errors, configErrors };
  }

  // Check that at least one config exists when enabled
  const configs = eventDetection.event_configs || [];
  if (configs.length === 0) {
    errors.push(validationError('oneEventRuleRequiredWhenEnabled', 'At least one event rule is required when event detection is enabled'));
    return { isValid: false, errors, configErrors };
  }

  // Validate each config
  configs.forEach((config, index) => {
    const validation = validateEventConfig(config, speedLimits, savedSpeedValues);
    if (!validation.isValid) {
      configErrors[index] = validation;
    }
  });

  // Check for duplicate event configs (same conditions, logic, and action)
  const duplicateConfigs = findDuplicateEventConfigs(configs);
  if (duplicateConfigs.length > 0) {
    errors.push(validationError('duplicateEventRules', 'Duplicate event rules found at positions: {{positions}}', {
      positions: duplicateConfigs.map(i => i + 1).join(', ')
    }));

    // Add errors to each duplicate config
    duplicateConfigs.forEach((index) => {
      if (!configErrors[index]) {
        configErrors[index] = { isValid: false, errors: [], conditionErrors: {} };
      }
      configErrors[index].errors.push(validationError('eventRuleDuplicate', 'This event rule is a duplicate of another rule'));
    });
  }

  const hasConfigErrors = Object.keys(configErrors).length > 0;

  return {
    isValid: errors.length === 0 && !hasConfigErrors,
    errors,
    configErrors
  };
};

/**
 * Find duplicate event configs
 * @param {Array} configs - Array of event config objects
 * @returns {Array} - Array of indices that have duplicates
 */
export const findDuplicateEventConfigs = (configs) => {
  if (!configs || configs.length < 2) return [];

  const duplicateIndices = new Set();
  const seen = new Map();

  configs.forEach((config, index) => {
    // Serialize the config for comparison, including coupling metadata.
    const conditionsStr = config.conditions
      .map(c => serializeCondition(c))
      .sort()
      .join('||');

    // Include coupling metadata to distinguish alarm, dashboard, and manual rules.
    const couplingStr = config.coupling_config ? JSON.stringify(config.coupling_config, Object.keys(config.coupling_config).sort()) : 'null';
    const dashboardCouplingStr = config.dashboard_coupling_config ? JSON.stringify(config.dashboard_coupling_config, Object.keys(config.dashboard_coupling_config).sort()) : 'null';

    const serialized = `${conditionsStr}|${config.logic}|${config.action}|${couplingStr}|${dashboardCouplingStr}`;

    if (seen.has(serialized)) {
      // Mark both original and duplicate
      duplicateIndices.add(seen.get(serialized));
      duplicateIndices.add(index);
    } else {
      seen.set(serialized, index);
    }
  });

  return Array.from(duplicateIndices);
};

/**
 * Normalize threshold values to 2 decimal places
 * @param {Object} thresholds - Threshold object
 * @returns {Object} - Normalized thresholds
 */
const normalizeThresholds = (thresholds) => {
  if (!thresholds) return {};

  const normalized = {};
  Object.keys(thresholds).forEach((key) => {
    const value = thresholds[key];
    if (typeof value === 'number') {
      normalized[key] = parseFloat(value.toFixed(2));
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects (e.g., min/max for range operators)
      normalized[key] = normalizeThresholds(value);
    } else {
      normalized[key] = value;
    }
  });
  return normalized;
};

/**
 * Normalize event detection config for comparison (removes undefined/null, sorts arrays)
 * @param {Object} eventDetection - Event detection config
 * @returns {Object} - Normalized config
 */
export const normalizeEventDetection = (eventDetection) => {
  if (!eventDetection) return null;

  const normalized = {
    enabled: eventDetection.enabled || false,
    event_configs: (eventDetection.event_configs || []).map(config => ({
      conditions: (config.conditions || []).map(condition => ({
        metric: condition.metric,
        metricType: condition.metricType,
        operator: condition.operator,
        axis: condition.axis ? [...condition.axis].sort() : [],
        thresholds: normalizeThresholds(condition.thresholds || {})
      })),
      logic: config.logic,
      action: config.action,
      // Include coupling metadata if present
      ...(config.coupling_config && { coupling_config: config.coupling_config }),
      ...(config.dashboard_coupling_config && { dashboard_coupling_config: config.dashboard_coupling_config })
    }))
  };

  return normalized;
};

/**
 * Compare two event detection configs for equality
 * @param {Object} config1 - First event detection config
 * @param {Object} config2 - Second event detection config
 * @returns {boolean} - True if configs are equal
 */
export const areEventDetectionsEqual = (config1, config2) => {
  const normalized1 = normalizeEventDetection(config1);
  const normalized2 = normalizeEventDetection(config2);

  return JSON.stringify(normalized1) === JSON.stringify(normalized2);
};

/**
 * Get user-friendly error message
 * @param {string|Object} error - Error message or translation descriptor
 * @param {Function} t - Translation function
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyError = (error, t) => {
  if (!error || typeof error === 'string') {
    return error;
  }

  if (error.key && typeof t === 'function') {
    return t(error.key, {
      defaultValue: error.defaultValue,
      ...(error.values || {})
    });
  }

  return error.defaultValue || '';
};
